/**
 * Central router setup for the admin center.
 *
 * The router combines authentication routes, core application routes, admin
 * settings routes, and dynamically registered module routes. Global guards
 * enforce session state, role-based access control, and redirect handling.
 */

import { createRouter, createWebHistory } from 'vue-router'
import { useAuth, checkSessionStore } from '../composables/useAuth'
import { useSessionStore } from '../store/sessionStore'
import { useModuleStore } from '../store/moduleStore'
import { useThemeStore } from '../store/themeStore'
import mainRoutes from './mainRoutes'
import authRoutes from './authRoutes'
import adminRoutes from './adminRoutes'
import { getStoredRedirectSessionPayload, replaceRedirect } from './redirect'
import BackendTemplate from '../layouts/BackendTemplate.vue'
import UnauthorizedAccess from '../pages/errors/UnauthorizedAccess.vue'
import NotFoundPage from '../pages/errors/NotFoundPage.vue'
import { installI18nRouterGuard } from '../plugins/i18n/routerGuard'

const routes = [
  ...authRoutes,
  {
    path: '/',
    component: BackendTemplate,
    meta: { title: 'Admin-Center', requiresAuth: true },
    children: [...mainRoutes, ...adminRoutes]
  },
  {
    path: '/unauthorized',
    component: UnauthorizedAccess,
    meta: { title: 'Unauthorized' }
  },
  {
    path: '/:catchAll(.*)',
    component: NotFoundPage,
    meta: { title: 'Page Not Found' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

installI18nRouterGuard(router)

router.beforeEach(async (to, from, next) => {
  const { isAuthenticated, checkIfAuthenticated } = useAuth()

  try {
    await checkSessionStore(isAuthenticated)
    await checkIfAuthenticated()

    // Module state must be ready before routes relying on dynamic module menus render.
    if (isAuthenticated.value) {
      const moduleStore = useModuleStore()
      if (!moduleStore.isLoaded) {
        await moduleStore.initialize()
      }

      const themeStore = useThemeStore()
      try {
        await themeStore.initialize()
      } catch (error) {
        console.error('Fehler beim Laden des Tenant-Themes:', error)
      }
    }
  } catch (error) {
    console.error('Fehler bei der Authentifizierung:', error)
    return next('/login')
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const sessionStore = useSessionStore()
  const userRole = (sessionStore.user as { role?: string } | null)?.role ?? null

  if (requiresAuth && !isAuthenticated.value) {
    const encodedRedirect = encodeURIComponent(to.fullPath)
    return next(`/login?redirect=${encodedRedirect}`)
  }

  const allowedRoles = Array.isArray(to.meta.roles) ? (to.meta.roles as string[]) : undefined
  if (allowedRoles && (!userRole || !allowedRoles.includes(userRole))) {
    return next('/unauthorized')
  }

  // Protect internal translation languages so tenant-scoped users cannot edit
  // AI Lab owned language packs through the shared translation UI.
  console.log(to.path)
  if (to.path.includes('/tenant/translations/')) {
    const allowedRolesForLanguages = ['ai_lab_admin', 'ai_lab_user']
    const adminLanguages = ['670e1e32d4634dd3c3ec7478', '670fad078be48456eb53b250']
    const destinationLanguage = Array.isArray(to.params.translation)
      ? to.params.translation[0]
      : to.params.translation
    if (
      allowedRolesForLanguages &&
      adminLanguages.includes(destinationLanguage) &&
      (!userRole || !allowedRolesForLanguages.includes(userRole))
    ) {
      return next('/unauthorized')
    }
  }

  next()
})

router.afterEach((to) => {
  const { isAuthenticated } = useAuth()

  if (to.path === '/login' && isAuthenticated.value) {
    void replaceRedirect(router, to.query.redirect, getStoredRedirectSessionPayload())
  }
})

export default router
